// apps/client/src/hooks/pipelineTracker/useOperationsOverview.ts
//
// Operations > Overview: the figures the exports can support today (compliance register and
// watchlist, broker efficiency by month, the tasks tile, the analyst view, days in queue), plus
// the tenant-entered monthly inputs and the targets they are graded against.
//
// The wire types mirror services/client-functions/src/pipelineTracker/reports/
// operationsOverviewQuery.ts and operationsOverviewReport.ts. Declared here, not imported, for the
// same two reasons as every other tracker hook: the browser bundle cannot reach the functions
// package, and the tracker isolation fence forbids a shared one.

import { useCallback, useMemo } from 'react';
import { REPORT_CALLABLE, TAXONOMY_CALLABLE } from './reportContract';
import { GET_MONTHLY_INPUTS_ACTION, OPERATIONS_OVERVIEW_REPORT_ACTION } from './operationsContract';
import {
  DEFAULT_RAG,
  GET_TARGETS_ACTION,
  type MonthlyInputTargets,
  type RagThresholds,
  type TargetsDoc,
} from './targetsContract';
import { useTrackerCall } from './reportCache';

export type ComplianceGroupKey = 'annual_review' | 'interest_only_expiry' | 'facility_expiry';

export interface ComplianceItem {
  taskId: string;
  dealRef: string | null;
  groupKey: ComplianceGroupKey;
  groupLabel: string;
  owner: string;
  dueDate: string;
  days: number;
}

export interface ComplianceGroupStatus {
  key: ComplianceGroupKey;
  label: string;
  watchDays: number;
  mapped: boolean;
  overdueCount: number | null;
  watchCount: number | null;
}

export interface ComplianceWatchGroup {
  key: ComplianceGroupKey;
  label: string;
  watchDays: number;
  rows: ComplianceItem[];
  total: number;
}

export interface ComplianceRegisterData {
  asAtDate: string;
  groups: ComplianceGroupStatus[];
  register: { rows: ComplianceItem[]; total: number };
  watchlist: ComplianceWatchGroup[];
  unmappedTaskCount: number;
}

export interface EfficiencySeries {
  key: string;
  name: string;
  kind: 'broker' | 'group';
  leads: number[];
  submissions: number[];
  settlements: number[];
}

export interface BrokerEfficiencyData {
  asAtDate: string;
  months: string[];
  from: string;
  to: string;
  series: EfficiencySeries[];
  other: { leads: number[]; submissions: number[]; settlements: number[] };
  totals: { leads: number[]; submissions: number[]; settlements: number[] };
}

export interface AnalystFigures {
  inFlight: number;
  /** Integer cents. */
  inFlightValueCents: number;
  /** Integer cents, settled in the twelve months. */
  settledValueCents: number;
  submissions: number[];
  approvals: number[];
  settlements: number[];
  deals: number;
}

export interface AnalystRow extends AnalystFigures {
  label: string;
  name: string;
}

export interface AnalystViewData {
  asAtDate: string;
  months: string[];
  from: string;
  to: string;
  configured: boolean;
  analysts: AnalystRow[];
  labelled: AnalystFigures;
  unlabelled: AnalystFigures;
  sharedDeals: number;
  unseenLabels: string[];
  availableLabels: { label: string; deals: number }[];
}

export interface QueueStageColumn {
  key: string;
  label: string;
  order: number;
  /** False when the tenant's taxonomy has no such stage: the column shows no figure, not zero. */
  present: boolean;
}

export interface QueueStageFigures {
  deals: number;
  medianDays: number | null;
  p75Days: number | null;
  oldestDays: number | null;
  /** Deals with no stage-entered date: counted in `deals`, in none of the day figures. */
  missingDate: number;
  /** Deals dated after the snapshot: clamped to 0 days and counted here. */
  futureDate: number;
}

export interface QueueRow {
  key: string;
  name: string;
  /** One per `QueueViewData.stages`, same order. */
  stages: QueueStageFigures[];
  deals: number;
}

export interface QueueByAnalyst {
  configured: boolean;
  rows: QueueRow[];
  labelled: QueueStageFigures[];
  unlabelled: QueueStageFigures[];
  sharedDeals: number;
}

export interface QueueByLender {
  rows: QueueRow[];
  notRecorded: QueueStageFigures[];
  recordedDeals: number;
  coveragePct: number | null;
  lowCoverage: boolean;
  /** Null when no Sales export matched the snapshot: the lender is Performance's alone. */
  salesAsAtDate: string | null;
}

export interface QueueViewData {
  asAtDate: string;
  stages: QueueStageColumn[];
  deals: number;
  all: QueueStageFigures[];
  missingDateDeals: number;
  futureDateDeals: number;
  byAnalyst: QueueByAnalyst;
  byLender: QueueByLender;
}

export interface OverviewTaskTotals {
  asAtDate: string;
  window: { start: string; end: string };
  doneInWindow: number;
  dueInWindow: number;
  outstanding: number;
  overdue: number;
}

export type NoSnapshot = { status: 'no_snapshot'; reason: 'never_ingested' | 'not_committed' };

export type OperationsOverviewResponse =
  | {
      status: 'ok';
      tasks: ({ status: 'ok' } & OverviewTaskTotals) | NoSnapshot;
      compliance: ({ status: 'ok' } & ComplianceRegisterData) | NoSnapshot;
      efficiency: ({ status: 'ok' } & BrokerEfficiencyData) | NoSnapshot;
      /**
       * Optional on the wire type only so a response cached by an older deployment still renders:
       * absent reads as "not yet available", never as zero analysts.
       */
      analysts?: ({ status: 'ok' } & AnalystViewData) | NoSnapshot;
      /** Optional for the same reason: a cached response from before the queue existed still renders. */
      queue?: ({ status: 'ok' } & QueueViewData) | NoSnapshot;
    }
  | { status: 'invalid_request'; reason: string };

/** One month's tenant-entered figures, per the shared settings contract. */
export interface MonthlyInputEntry {
  googleReviewCount?: number;
  googleRating?: number;
  sfgAuditPct?: number;
}

export type MonthlyInputs = Record<string, MonthlyInputEntry>;

/**
 * Read the `months` map out of a getMonthlyInputs response.
 *
 * Tolerant of the envelope, deliberately: the settings dispatcher is built by the targets work in
 * parallel with this view, and the contract fixes the DOCUMENT (`{ months: { 'YYYY-MM': {...} } }`)
 * but not whether the callable returns it bare or wrapped. Anything that is not a month map reads
 * as "nothing entered", which is the honest rendering of a figure we could not find.
 */
export function extractMonthlyInputs(payload: unknown): MonthlyInputs {
  const candidates: unknown[] = [payload];
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    candidates.push(p.monthlyInputs, p.document, p.data, p.inputs);
  }
  for (const c of candidates) {
    if (c && typeof c === 'object' && 'months' in (c as object)) {
      const months = (c as { months: unknown }).months;
      if (months && typeof months === 'object' && !Array.isArray(months)) {
        const out: MonthlyInputs = {};
        for (const [key, value] of Object.entries(months as Record<string, unknown>)) {
          if (!/^\d{4}-\d{2}$/.test(key) || !value || typeof value !== 'object') continue;
          const v = value as Record<string, unknown>;
          const entry: MonthlyInputEntry = {};
          if (typeof v.googleReviewCount === 'number' && Number.isFinite(v.googleReviewCount)) {
            entry.googleReviewCount = v.googleReviewCount;
          }
          if (typeof v.googleRating === 'number' && Number.isFinite(v.googleRating)) {
            entry.googleRating = v.googleRating;
          }
          if (typeof v.sfgAuditPct === 'number' && Number.isFinite(v.sfgAuditPct)) {
            entry.sfgAuditPct = v.sfgAuditPct;
          }
          out[key] = entry;
        }
        return out;
      }
    }
  }
  return {};
}

/** What the monthly tiles are graded against: the targets document's input targets and thresholds. */
export interface MonthlyInputGrading {
  targets: MonthlyInputTargets;
  rag: RagThresholds;
}

/**
 * Read the input targets out of a getTargets response. Null when there is no targets document to
 * read or it sets none of the three, so the tiles carry no grade rather than one against zero.
 */
export function extractInputGrading(payload: unknown): MonthlyInputGrading | null {
  const doc = payload && typeof payload === 'object' ? (payload as { targets?: TargetsDoc }).targets : undefined;
  if (!doc || typeof doc !== 'object') return null;
  const given = doc.monthlyInputTargets;
  if (!given || typeof given !== 'object') return null;
  const targets: MonthlyInputTargets = {};
  for (const key of ['googleReviewCount', 'googleRating', 'sfgAuditPct'] as const) {
    const value = given[key];
    if (typeof value === 'number' && Number.isFinite(value)) targets[key] = value;
  }
  if (Object.keys(targets).length === 0) return null;
  const rag = doc.rag;
  const validRag =
    rag && typeof rag.greenAtPct === 'number' && typeof rag.amberAtPct === 'number' ? rag : DEFAULT_RAG;
  return { targets, rag: { greenAtPct: validRag.greenAtPct, amberAtPct: validRag.amberAtPct } };
}

export interface UseOperationsOverviewReturn {
  response: OperationsOverviewResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  /** Tenant-entered figures by 'YYYY-MM'. Empty when none are entered or none could be read. */
  monthlyInputs: MonthlyInputs;
  /** Set when the monthly inputs could not be read at all, which is different from none entered. */
  monthlyInputsError: string | null;
  /** The input targets and thresholds; null when none are set or they could not be read. */
  inputGrading: MonthlyInputGrading | null;
  /** Set when the targets could not be read, so the tiles can say they are not graded. */
  inputTargetsError: string | null;
}

/**
 * Load the Overview and the monthly inputs together, in parallel.
 *
 * Two callables, two failure modes, reported separately: an unreadable monthly-inputs document
 * must not blank the register, and a failed report must not be dressed up as "nothing entered".
 * Both go through the session cache (reportCache.ts), so returning to the tab is immediate.
 */
export function useOperationsOverview(companyId: string): UseOperationsOverviewReturn {
  const report = useTrackerCall<OperationsOverviewResponse>(
    REPORT_CALLABLE,
    companyId ? { action: OPERATIONS_OVERVIEW_REPORT_ACTION, companyId } : null,
    {
      label: 'useOperationsOverview',
      // Surfaced, never swallowed: an empty register here would read as "nothing overdue".
      describeError: (err) =>
        err instanceof Error ? err.message : 'Failed to load the Operations overview',
    }
  );
  const inputs = useTrackerCall<unknown>(
    TAXONOMY_CALLABLE,
    companyId ? { action: GET_MONTHLY_INPUTS_ACTION, companyId } : null,
    {
      label: 'useOperationsOverview monthly inputs',
      describeError: (err) =>
        err instanceof Error ? err.message : 'The monthly inputs could not be read',
    }
  );
  const targets = useTrackerCall<unknown>(
    TAXONOMY_CALLABLE,
    companyId ? { action: GET_TARGETS_ACTION, companyId } : null,
    {
      label: 'useOperationsOverview targets',
      describeError: (err) =>
        err instanceof Error ? err.message : 'The targets could not be read',
    }
  );
  const { refetch: refetchReport } = report;
  const { refetch: refetchInputs } = inputs;
  const { refetch: refetchTargets } = targets;
  const refetch = useCallback(() => {
    refetchReport();
    refetchInputs();
    refetchTargets();
  }, [refetchReport, refetchInputs, refetchTargets]);
  const monthlyInputs = useMemo(
    () => (inputs.error || inputs.data === null ? {} : extractMonthlyInputs(inputs.data)),
    [inputs.data, inputs.error]
  );
  const inputGrading = useMemo(
    () => (targets.error || targets.data === null ? null : extractInputGrading(targets.data)),
    [targets.data, targets.error]
  );

  return {
    response: report.error ? null : report.data,
    loading: report.loading,
    error: report.error,
    refetch,
    monthlyInputs,
    monthlyInputsError: inputs.error,
    inputGrading,
    inputTargetsError: targets.error,
  };
}
